import { fetchEntity, fetchEntityList } from '@/lib/entity-api';
import { getUserId } from '@/lib/auth-session';
import { GanttTask, GanttProject, GanttDependency } from './types';
import { getApiUrl } from '@/lib/api-config';
import { getSelectedCompanyId } from '@/lib/active-company';

// Save a single task to the database
export const saveGanttTask = async (projectId: string, task: GanttTask, companyId: string, userId: string): Promise<GanttTask | null> => {
  try {
    const taskId = isValidUUID(task.id) ? task.id : generateUUID();
    if (!isValidUUID(task.id)) {
      console.log(`Converting invalid UUID "${task.id}" to "${taskId}"`);
    }
    
    const taskData = {
      // Always provide a valid UUID - generate one if the task doesn't have a valid one
      id: taskId,
      project_id: projectId,
      name: task.name,
      item_type: task.type,
      parent_id: task.parentId || null,
      start_date: task.startDate,
      end_date: task.endDate,
      duration: task.duration,
      progress: task.progress,
      status: task.status,
      color: task.color,
      description: (task as any).description || null,
      crew: task.crew || null,
      equipment: task.equipment || [],
      notes: task.notes || null,
      is_expanded: (task as any).isExpanded ?? true,
      sort_order: (task as any).sortOrder ?? 0,
      level: (task as any).level ?? 0,
      assignee_id: (task as any).assigneeId ?? null,
      baseline_start_date: (task as any).baselineStartDate ?? null,
      baseline_end_date: (task as any).baselineEndDate ?? null,
      company_id: companyId,
      created_by: userId,
      updated_by: userId
    };

    // Use generic API for schedule task creation/update
    const isUpdate = isValidUUID(task.id);
    const url = isUpdate 
      ? getApiUrl(`api/entities/schedule_task/${taskId}`)
      : getApiUrl('api/entities/schedule_task');
    const method = isUpdate ? 'PUT' : 'POST';

    // Remove company_id from taskData to avoid duplication
    const { company_id, ...taskDataWithoutCompanyId } = taskData;

    const response = await fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        company_id: companyId,
        ...taskDataWithoutCompanyId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error saving Gantt task:', errorData);
      return null;
    }

    const result = await response.json();
    return convertDbTaskToGanttTask(result.data);
  } catch (error) {
    console.error('Error saving Gantt task:', error);
    return null;
  }
};

// Save multiple tasks to the database
export const saveGanttTasks = async (projectId: string, tasks: GanttTask[], companyId: string, userId: string): Promise<GanttTask[]> => {
  try {
    console.log(`Saving ${tasks.length} tasks for project ${projectId}`, { companyId, userId });
    const tasksToSave = tasks.map(task => {
      const taskId = isValidUUID(task.id) ? task.id : generateUUID();
      if (!isValidUUID(task.id)) {
        console.log(`Converting invalid UUID "${task.id}" to "${taskId}"`);
      }
      
      // For upsert, we need to provide created_by for new tasks
      // The database will handle the conflict resolution
      return {
        id: taskId,
        project_id: projectId,
        name: task.name,
        item_type: task.type,
        parent_id: task.parentId || null,
        start_date: task.startDate,
        end_date: task.endDate,
        duration: task.duration,
        progress: task.progress,
        status: task.status,
        color: task.color,
        description: (task as any).description || null,
        crew: task.crew || null,
        equipment: task.equipment || [],
        notes: task.notes || null,
        is_expanded: (task as any).isExpanded ?? true,
        sort_order: (task as any).sortOrder ?? 0,
        level: (task as any).level ?? 0,
        auto_progress_update: (task as any).autoProgressUpdate ?? true,
        assignee_id: (task as any).assigneeId ?? null,
        baseline_start_date: (task as any).baselineStartDate ?? null,
        baseline_end_date: (task as any).baselineEndDate ?? null,
        company_id: companyId,
        created_by: userId, // This will be ignored on conflict due to the constraint
        updated_by: userId,
        updated_at: new Date().toISOString()
      };
    });

    console.log('Tasks to save:', tasksToSave.map(t => ({ id: t.id, name: t.name, type: t.item_type, parent_id: t.parent_id })));

    // Use individual API calls for each task (bulk API not implemented yet)
    const savedTasks: GanttTask[] = [];
    for (const taskData of tasksToSave) {
      const isUpdate = isValidUUID(taskData.id);
      const url = isUpdate 
        ? getApiUrl(`api/entities/schedule_task/${taskData.id}`)
        : getApiUrl('api/entities/schedule_task');
      const method = isUpdate ? 'PUT' : 'POST';

      try {
        // Remove company_id and updated_at from taskData to avoid duplication
        const { company_id, updated_at, ...taskDataWithoutCompanyId } = taskData;
        
        const response = await fetch(url, {
          method: method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            company_id: companyId,
            ...taskDataWithoutCompanyId
          })
        });

        if (response.ok) {
          const result = await response.json();
          savedTasks.push(convertDbTaskToGanttTask(result.data));
        } else {
          const errorData = await response.json();
          console.error(`Error saving task ${taskData.id}:`, errorData);
        }
      } catch (error) {
        console.error(`Error saving task ${taskData.id}:`, error);
      }
    }

    console.log('Successfully saved tasks:', savedTasks.map(t => ({ id: t.id, name: t.name, type: t.type, parentId: t.parentId })));
    return savedTasks;
  } catch (error) {
    console.error('Error saving Gantt tasks:', error);
    return [];
  }
};

// Helper function to check if a string is a valid UUID
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

// Helper function to generate a proper UUID
const generateUUID = (): string => {
  return crypto.randomUUID();
};

// Group expansion state is handled locally in the component
// No database persistence needed for UI state

// Load all tasks for a project
export const loadGanttTasks = async (projectId: string): Promise<GanttTask[]> => {
  try {
    // The backend answers the project's live tasks (GET /api/entities/schedule_task); the Gantt order is level, then sort_order
    const rows = await fetchEntityList<any>('schedule_task', { projectId });
    const data = [...rows].sort((a, b) => (a.level ?? 0) - (b.level ?? 0) || (a.sort_order ?? 0) - (b.sort_order ?? 0));

    console.log('Loaded tasks from database:', data.map(t => ({ id: t.id, name: t.name, type: t.item_type, parent_id: t.parent_id })));
    return data.map(convertDbTaskToGanttTask);
  } catch (error) {
    console.error('Error loading Gantt tasks:', error);
    return [];
  }
};

// Load dependencies for a project from schedule_dependencies (phase97) --
// the ONLY representation of a task relationship in this app (carries
// type FS/SS/FF/SF and lag_days). No fallback: predecessors/successors
// array columns on schedule_tasks were dropped (phase100, 2026-09-30),
// so there is nothing left to reconstruct from if this query fails --
// a failure here is a real error, surfaced as an empty list with a clear
// log rather than silently fabricated from data that no longer exists.
export const loadGanttDependencies = async (projectId: string): Promise<GanttDependency[]> => {
  let data: any[];
  try {
    data = await fetchEntityList<any>('schedule_dependency', { projectId });
  } catch (error) {
    console.error('Error loading dependencies from schedule_dependencies:', error);
    return [];
  }

  return data.map((d: any) => ({
    id: d.id,
    fromTaskId: d.from_task_id,
    toTaskId: d.to_task_id,
    type: d.dependency_type,
    lagDays: d.lag_days ?? 0,
  }));
};

// Group expansion states are handled locally in the component
// No database persistence needed for UI state

// Delete a task (soft delete)
export const deleteGanttTask = async (taskId: string): Promise<boolean> => {
  try {
    // Get user info for the API call
    const userId = getUserId();
    if (!userId) {
      console.error('No authenticated user found');
      return false;
    }

    // Use generic API for schedule task deletion
    const response = await fetch(getApiUrl(`api/entities/schedule_task/${taskId}`), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        company_id: getSelectedCompanyId()
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error deleting Gantt task:', errorData);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting Gantt task:', error);
    return false;
  }
};

// Helper function to convert database task to GanttTask
const convertDbTaskToGanttTask = (dbTask: any): GanttTask => {
  return {
    id: dbTask.id,
    name: dbTask.name,
    type: dbTask.item_type, // Updated column name
    parentId: dbTask.parent_id,
    startDate: dbTask.start_date,
    endDate: dbTask.end_date,
    duration: dbTask.duration,
    progress: dbTask.progress,
    status: dbTask.status,
    crew: dbTask.crew,
    equipment: dbTask.equipment || [],
    notes: dbTask.notes,
    color: dbTask.color || 'bg-blue-200',
    description: dbTask.description,
    // Add new fields for groups and ordering
    isExpanded: dbTask.is_expanded,
    sortOrder: dbTask.sort_order,
    level: dbTask.level,
    autoProgressUpdate: dbTask.auto_progress_update ?? true,
    assigneeId: dbTask.assignee_id ?? null,
    baselineStartDate: dbTask.baseline_start_date ?? null,
    baselineEndDate: dbTask.baseline_end_date ?? null
  };
};

// Create a dependency: a real row in schedule_dependencies (carries
// type + lag_days, phase97) -- the only representation of a task
// relationship in this app. No array mirror on schedule_tasks; that
// column pair was dropped (phase100, 2026-09-30).
export const createGanttDependency = async (projectId: string, dependency: GanttDependency, companyId: string, userId: string): Promise<boolean> => {
  try {
    const depResponse = await fetch(getApiUrl('api/entities/schedule_dependency'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        company_id: companyId,
        project_id: projectId,
        from_task_id: dependency.fromTaskId,
        to_task_id: dependency.toTaskId,
        dependency_type: dependency.type,
        lag_days: dependency.lagDays ?? 0,
      }),
    });
    if (!depResponse.ok) {
      const errorData = await depResponse.json();
      console.error('Error creating schedule_dependency row:', errorData);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error creating dependency:', error);
    return false;
  }
};

// Update an existing dependency's type/lag in place (Gantt's "Depends on"
// editor) -- same generic entity PUT route createGanttDependency's POST
// already goes through (api/entities/schedule_dependency), no new backend
// endpoint needed. Only type/lag change; from/to task never do (that would
// be a different dependency, not an edit of this one).
export const updateGanttDependency = async (
  dependency: GanttDependency,
  patch: { type?: GanttDependency['type']; lagDays?: number },
  companyId: string,
  userId: string
): Promise<boolean> => {
  try {
    const response = await fetch(getApiUrl(`api/entities/schedule_dependency/${dependency.id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        company_id: companyId,
        ...(patch.type !== undefined ? { dependency_type: patch.type } : {}),
        ...(patch.lagDays !== undefined ? { lag_days: patch.lagDays } : {}),
      }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error updating schedule_dependency row:', errorData);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error updating dependency:', error);
    return false;
  }
};

// Delete a dependency: soft-deletes its schedule_dependencies row
// (looked up by from/to task id, not by dependency.id, since callers may
// only have the task pair).
export const deleteGanttDependency = async (projectId: string, dependency: GanttDependency, companyId: string, userId: string): Promise<boolean> => {
  try {
    console.log('[GanttDB] deleteGanttDependency called', { projectId, from: dependency.fromTaskId, to: dependency.toTaskId });

    const projectDependencies = await fetchEntityList<any>('schedule_dependency', { projectId });
    const depRow = projectDependencies.find((d) => d.from_task_id === dependency.fromTaskId && d.to_task_id === dependency.toTaskId);

    if (depRow?.id) {
      const delResponse = await fetch(getApiUrl(`api/entities/schedule_dependency/${depRow.id}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, company_id: companyId }),
      });
      if (!delResponse.ok) {
        const errorData = await delResponse.json();
        console.error('Error deleting schedule_dependency row:', errorData);
        return false;
      }
    }

    console.log('[GanttDB] deleteGanttDependency succeeded', { projectId, from: dependency.fromTaskId, to: dependency.toTaskId });
    return true;
  } catch (error) {
    console.error('[GanttDB] deleteGanttDependency failed', error);
    return false;
  }
};

// Clean up orphaned reverse dependencies (for migration from old system)
// No cleanup needed; there is no dependencies table

// Load project with all tasks and dependencies
export const loadGanttProject = async (projectId: string): Promise<GanttProject | null> => {
  try {
    // Load project details
    let projectData: { id: string; name: string; start_date?: string | null; end_date?: string | null };
    try {
      projectData = await fetchEntity<any>('project', projectId);
    } catch (projectError) {
      console.error('Error loading project:', projectError);
      // Return empty project instead of null
      return {
        id: projectId,
        name: `Project ${projectId}`,
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        tasks: [],
        dependencies: []
      };
    }

    // Load tasks
    const tasks = await loadGanttTasks(projectId);
    console.log(`Loaded ${tasks.length} tasks for project ${projectId}`);
    
    // Load dependencies
    const dependencies = await loadGanttDependencies(projectId);

    const result = {
      id: projectData.id,
      name: projectData.name,
      startDate: projectData.start_date || new Date().toISOString().split('T')[0],
      endDate: projectData.end_date || new Date().toISOString().split('T')[0],
      tasks: tasks || [],
      dependencies: dependencies || []
    };
    
    console.log('Returning project data:', { 
      id: result.id, 
      name: result.name, 
      taskCount: result.tasks.length,
      tasks: result.tasks.map(t => ({ id: t.id, name: t.name, type: t.type }))
    });
    
    return result;
  } catch (error) {
    console.error('Error loading Gantt project:', error);
    // Return empty project instead of null
    return {
      id: projectId,
      name: `Project ${projectId}`,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      tasks: [],
      dependencies: []
    };
  }
};

